/**
 * Search Entities Hook
 *
 * Combines Redux selectors, filtering, ranking, and grouping for search.
 * Memoized to prevent unnecessary recalculations on every render.
 * Supports watchlist filtering via Zustand store.
 */

import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useAllArbitrageConfigs } from '@/entities/arbitrage';
import { type EnrichedPair, filterPairsByQuery, selectAllPairsForSearch } from '@/entities/search';
import { useAllBalancerTasks } from '@/entities/wallet-balancer';
import { createWatchlistKey, useWatchlistStore } from '@/entities/watchlist';

import { rankSearchResults } from '../model/searchRanking';
import {
  type ArbitrageSearchResult,
  type BalancerSearchResult,
  type DisplayMode,
  type EntityTypeFilter,
  type PairSearchResult,
  type ProjectSearchResult,
  type SearchResult,
} from '../types';

// Total count of all platforms (CEX: 17 + DEX: 17 + SOL: 2 = 36)
// When all platforms are selected, treat as "no filter" (show all)
const ALL_PLATFORMS_COUNT = 36;

interface UseSearchEntitiesProps {
  /** Search query string (debounced) */
  searchQuery: string;

  /** Display mode: which project set to show (watchlist/active/archive) */
  displayMode?: DisplayMode;

  /** Entity type filters: which entity types to show within display mode (empty = all) */
  typeFilters?: EntityTypeFilter[];

  /** Optional platform filters (platform substring match, lowercased) */
  platformFilters?: string[];

  /** Only show active arbitrages when true */
  activeArbitrageOnly?: boolean;

  /** Only show active balancers when true */
  activeBalancerOnly?: boolean;

  /** Whether to enable data fetching (for lazy loading when dropdown opens) */
  enabled?: boolean;
}

interface UseSearchEntitiesReturn {
  /** Filtered and ranked pair results */
  pairs: PairSearchResult[];

  /** Filtered and ranked arbitrage results */
  arbitrages: ArbitrageSearchResult[];

  /** Filtered and ranked balancer results */
  balancers: BalancerSearchResult[];

  /** Project links (for "all pairs" queries) */
  projects?: ProjectSearchResult[];

  /** Combined results for unified display */
  allResults: SearchResult[];

  /** Whether search is actively filtering (has query) */
  isSearching: boolean;

  /** Total number of results across all categories */
  totalResults: number;

  /** Loading state */
  isLoading: boolean;

  /** Set of favorited pair keys (format: "projectId-pairId") */
  favoritePairsSet: Set<string>;

  /** Set of favorited arbitrage keys (format: "projectId-arbitrageId") */
  favoriteArbitragesSet: Set<string>;

  /** Set of favorited balancer keys (format: "projectId-balancerId") */
  favoriteBalancersSet: Set<string>;

  /** Total pairs count per project (for "All pairs" badge) */
  projectTotalPairs: Map<number, number>;

  /**
   * Refresh bots data for a single project (arbitrages + balancers) and update caches.
   * Intended to be called when user expands a project group (avoid fetching all projects at once).
   */
  refreshProjectBotsData: (projectId: number) => Promise<void>;
}

/**
 * Apply display mode filter to pairs (stage 1)
 */
function applyDisplayModeFilter(
  pairs: EnrichedPair[],
  displayMode: DisplayMode,
  favoritePairKeys?: Set<string>,
): EnrichedPair[] {
  switch (displayMode) {
    case 'watchlist':
      return favoritePairKeys
        ? pairs.filter(p => favoritePairKeys.has(createWatchlistKey(p.projectId, p.pairId)))
        : [];
    case 'active':
      return pairs.filter(p => !p.isArchived);
    case 'archive':
      return pairs.filter(p => p.isArchived);
    default:
      return pairs;
  }
}

function buildProjectResults(pairs: EnrichedPair[]): ProjectSearchResult[] {
  const map = new Map<
    number,
    { result: ProjectSearchResult; hasActivePair: boolean; hasArchivedPair: boolean }
  >();

  pairs.forEach(p => {
    let entry = map.get(p.projectId);
    if (!entry) {
      entry = {
        result: {
          id: p.projectId,
          type: 'project',
          projectId: p.projectId,
          name: p.projectName,
          pairsCount: 0,
          status: 'active',
          matchScore: 0,
          matchType: 'substring',
        },
        hasActivePair: false,
        hasArchivedPair: false,
      };
      map.set(p.projectId, entry);
    }

    entry.result.pairsCount += 1;
    if (p.isArchived) {
      entry.hasArchivedPair = true;
    } else {
      entry.hasActivePair = true;
    }
  });

  // Determine status consistently: active if ANY pair is active, archived only if ALL are archived
  return Array.from(map.values()).map(({ result, hasActivePair }) => ({
    ...result,
    status: hasActivePair ? 'active' : 'archived',
  }));
}

/**
 * Filter arbitrages by query
 */
function filterArbitragesByQuery(
  arbitrages: ArbitrageSearchResult[],
  query: string,
): ArbitrageSearchResult[] {
  const lowerQuery = query.toLowerCase();
  return arbitrages.filter(
    arb =>
      arb.name.toLowerCase().includes(lowerQuery) ||
      arb.projectName.toLowerCase().includes(lowerQuery) ||
      String(arb.arbitrageId).includes(query) ||
      String(arb.projectId).includes(query),
  );
}

/**
 * Filter balancers by query
 */
function filterBalancersByQuery(
  balancers: BalancerSearchResult[],
  query: string,
): BalancerSearchResult[] {
  const lowerQuery = query.toLowerCase();
  return balancers.filter(
    bal =>
      bal.name.toLowerCase().includes(lowerQuery) ||
      bal.projectName.toLowerCase().includes(lowerQuery) ||
      String(bal.balancerId).includes(query) ||
      String(bal.projectId).includes(query),
  );
}

/**
 * Hook for searching across pairs, arbitrages, and balancers
 */
export function useSearchEntities({
  searchQuery,
  displayMode = 'active',
  typeFilters = [],
  platformFilters = [],
  enabled = true,
}: UseSearchEntitiesProps): UseSearchEntitiesReturn {
  // Get all pairs from Redux store (always available, no fetch needed)
  const allPairs = useSelector(selectAllPairsForSearch);

  // Check if projects have loaded (null means still loading from getAllProjectsAndPairs)
  const projectsLoaded = useSelector(
    (state: { projects: { projects: unknown[] | null } }) => state.projects.projects !== null,
  );

  // Get all arbitrages and balancers via React Query hooks (only when enabled)
  const {
    arbitrages: allArbitrages,
    isLoading: isLoadingArbitrages,
    refreshProject: refreshArbitrageProject,
  } = useAllArbitrageConfigs(enabled);
  const {
    balancers: allBalancers,
    isLoading: isLoadingBalancers,
    refreshProject: refreshBalancerProject,
  } = useAllBalancerTasks(enabled);

  // Get watchlist from Zustand store (now string keys)
  const favoritePairs = useWatchlistStore(s => s.favoritePairs);
  const favoriteArbitrages = useWatchlistStore(s => s.favoriteArbitrages);
  const favoriteBalancers = useWatchlistStore(s => s.favoriteBalancers);

  const favoritePairsSet = useMemo(() => new Set(favoritePairs), [favoritePairs]);
  const favoriteArbitragesSet = useMemo(() => new Set(favoriteArbitrages), [favoriteArbitrages]);
  const favoriteBalancersSet = useMemo(() => new Set(favoriteBalancers), [favoriteBalancers]);

  const pairSymbolById = useMemo(() => {
    const map = new Map<number, string>();
    (allPairs ?? []).forEach(pair => {
      map.set(pair.pairId, pair.symbol);
    });
    return map;
  }, [allPairs]);

  // Map arbitrages to search results
  const arbitrageResults = useMemo((): ArbitrageSearchResult[] => {
    return allArbitrages.map(arb => {
      const candidates = [...(arb.pairs_cex ?? []), ...(arb.pairs_dex ?? [])];
      let ticker: string | undefined;

      for (const pair of candidates) {
        ticker = pairSymbolById.get(pair.pair_id);
        if (ticker) break;
      }

      return {
        id: arb.id,
        type: 'arbitrage' as const,
        arbitrageId: arb.id,
        projectId: arb.project_id,
        projectName: arb.projectName,
        name: `Arbitrage #${arb.id} (${ticker ?? 'Stopped'})`,
        isArchived: arb.isArchived,
        status: arb.active ? ('active' as const) : ('inactive' as const),
        matchScore: 0,
        matchType: 'substring' as const,
      };
    });
  }, [allArbitrages, pairSymbolById]);

  // Map balancers to search results
  const balancerResults = useMemo((): BalancerSearchResult[] => {
    // Track per-project index to generate truly unique IDs
    const projectIndexMap = new Map<number, number>();

    return allBalancers.map(bal => {
      const groupId = bal.group?.id;

      // Get or initialize index for this project
      const projectIndex = projectIndexMap.get(bal.projectId) ?? 0;
      projectIndexMap.set(bal.projectId, projectIndex + 1);

      // Use group.id if available, otherwise generate unique ID using:
      // negative base (-1_000_000_000) + projectId * 10_000 + projectIndex
      // This supports up to 10,000 balancers per project without collision
      const uniqueId = groupId ?? -(1_000_000_000 + bal.projectId * 10_000 + projectIndex);

      return {
        id: uniqueId,
        type: 'balancer' as const,
        balancerId: uniqueId,
        projectId: bal.projectId,
        projectName: bal.projectName,
        name: groupId != null ? `Balancer #${groupId}` : `Balancer (${bal.projectName})`,
        isArchived: bal.isArchived,
        status: 'active' as const, // Balancers don't have is_active field
        matchScore: 0,
        matchType: 'substring' as const,
      };
    });
  }, [allBalancers]);

  // Memoize search results
  const searchResults = useMemo(() => {
    const mapPairToResult = (pair: EnrichedPair): PairSearchResult => ({
      id: pair.id,
      type: 'pair',
      pairId: pair.pairId,
      projectId: pair.projectId,
      projectName: pair.projectName,
      symbol: pair.symbol,
      baseToken: pair.baseToken,
      quoteToken: pair.quoteToken,
      pairType: pair.pairType,
      platform: pair.platform,
      network: pair.network,
      isFavorite: favoritePairsSet.has(createWatchlistKey(pair.projectId, pair.pairId)),
      isArchived: pair.isArchived,
      matchScore: 0,
      matchType: 'substring',
    });

    const trimmedQuery = searchQuery.trim();
    const lowerQuery = trimmedQuery.toLowerCase();
    // Recognize "all pairs", "pairs", or "all" as project search triggers
    const queryHasAllPairs =
      lowerQuery.includes('all pairs') ||
      lowerQuery === 'pairs' ||
      lowerQuery.startsWith('pairs ') ||
      lowerQuery.endsWith(' pairs') ||
      lowerQuery === 'all' ||
      lowerQuery.startsWith('all ') ||
      lowerQuery.endsWith(' all');
    const isSearching = trimmedQuery.length > 0;

    // Stage 1: Apply display mode filter to pairs
    // When searching (with a query), search all active projects instead of just watchlist
    // Only use archive filter when explicitly on archive tab
    const effectiveDisplayMode: DisplayMode =
      isSearching && displayMode === 'watchlist' ? 'active' : displayMode;
    const pairsByDisplayMode = applyDisplayModeFilter(
      allPairs ?? [],
      effectiveDisplayMode,
      favoritePairsSet,
    );

    // Stage 2: Apply filters - ADDITIVE logic (OR between filter types)
    // No filters = show all; ALL filters = show all; Partial filters = show only matching
    // Ignore legacy type filters (cex/dex/sol) stored before platform filters were split out
    const normalizedTypeFilters = typeFilters.filter(
      (filter): filter is 'arbitrage' | 'balancer' =>
        filter === 'arbitrage' || filter === 'balancer',
    );
    const noTypeFilters = normalizedTypeFilters.length === 0;
    const noPlatformFilters = platformFilters.length === 0;
    const noFiltersAtAll = noTypeFilters && noPlatformFilters;

    // Check if ALL filters are enabled (no restriction on any filter category)
    // Empty array = no restriction = equivalent to "all selected"
    const allTypeFiltersEnabled =
      noTypeFilters || // empty means no restriction on types
      (normalizedTypeFilters.length === 2 &&
        normalizedTypeFilters.includes('arbitrage') &&
        normalizedTypeFilters.includes('balancer'));
    const allPlatformsEnabled =
      noPlatformFilters || // empty means no restriction on platforms
      platformFilters.length === ALL_PLATFORMS_COUNT;
    const allFiltersEnabled = allTypeFiltersEnabled && allPlatformsEnabled;

    // Show everything when NO filters OR ALL filters are enabled
    const showAll = noFiltersAtAll || allFiltersEnabled;

    // Determine what to show based on active filters (additive/OR logic)
    // If showAll → show everything. Otherwise → show only what's explicitly enabled.
    // Empty typeFilters means "no restriction on types" = show all entity types
    const showArbitrages = showAll || noTypeFilters || normalizedTypeFilters.includes('arbitrage');
    const showBalancers = showAll || noTypeFilters || normalizedTypeFilters.includes('balancer');
    // Show pairs if: showAll, OR no type restriction, OR platform filters are active
    const showPairs = showAll || noTypeFilters || !noPlatformFilters;

    // Filter pairs by platform if platform filters are active (and not showing all)
    const filteredPairs = showPairs
      ? showAll || noPlatformFilters
        ? pairsByDisplayMode // No platform filter OR all platforms → all pairs
        : pairsByDisplayMode.filter(p =>
            platformFilters.some(f => p.platform.toLowerCase().includes(f)),
          )
      : [];

    // Filter arbitrages based on display mode (independent of platform filters!)
    // Arbitrages are shown if arbitrage filter is on OR no filters at all
    let filteredArbitrages: ArbitrageSearchResult[] = [];
    if (showArbitrages) {
      if (effectiveDisplayMode === 'watchlist') {
        filteredArbitrages = arbitrageResults.filter(a =>
          favoriteArbitragesSet.has(createWatchlistKey(a.projectId, a.arbitrageId)),
        );
      } else if (effectiveDisplayMode === 'archive') {
        filteredArbitrages = arbitrageResults.filter(a => a.isArchived);
      } else {
        filteredArbitrages = arbitrageResults.filter(a => !a.isArchived);
      }
    }

    // Filter balancers based on display mode (independent of platform filters!)
    // Balancers are shown if balancer filter is on OR no filters at all
    let filteredBalancers: BalancerSearchResult[] = [];
    if (showBalancers) {
      if (effectiveDisplayMode === 'watchlist') {
        filteredBalancers = balancerResults.filter(b =>
          favoriteBalancersSet.has(createWatchlistKey(b.projectId, b.balancerId)),
        );
      } else if (effectiveDisplayMode === 'archive') {
        filteredBalancers = balancerResults.filter(b => b.isArchived);
      } else {
        filteredBalancers = balancerResults.filter(b => !b.isArchived);
      }
    }

    // If no search query, return results sorted alphabetically
    if (!isSearching) {
      const defaultPairs = showPairs
        ? [...filteredPairs].sort((a, b) => a.symbol.localeCompare(b.symbol)).map(mapPairToResult)
        : [];

      const sortedArbitrages = [...filteredArbitrages].sort((a, b) => a.name.localeCompare(b.name));
      const sortedBalancers = [...filteredBalancers].sort((a, b) => a.name.localeCompare(b.name));

      return {
        pairs: defaultPairs,
        arbitrages: sortedArbitrages,
        balancers: sortedBalancers,
        allResults: [...defaultPairs, ...sortedArbitrages, ...sortedBalancers] as SearchResult[],
        isSearching: false,
        totalResults: defaultPairs.length + sortedArbitrages.length + sortedBalancers.length,
      };
    }

    // Filter pairs by query
    const queryFilteredPairs = showPairs ? filterPairsByQuery(filteredPairs, trimmedQuery) : [];

    // Convert EnrichedPair to PairSearchResult
    const pairSearchResults: PairSearchResult[] = queryFilteredPairs.map(mapPairToResult);

    // Rank and sort pair results
    const rankedPairs = rankSearchResults(pairSearchResults, trimmedQuery);

    // Filter and rank arbitrages
    const searchedArbitrages = filterArbitragesByQuery(filteredArbitrages, trimmedQuery);

    // Filter and rank balancers
    const searchedBalancers = filterBalancersByQuery(filteredBalancers, trimmedQuery);

    // Projects for "all pairs" / "pairs" queries
    let projectResults: ProjectSearchResult[] = [];
    if (queryHasAllPairs) {
      const projects = buildProjectResults(allPairs ?? []);
      // Remove trigger keywords and get remaining query for filtering
      const lowered = lowerQuery
        .replace(/all\s*pairs/g, '')
        .replace(/\bpairs\b/g, '')
        .replace(/\ball\b/g, '')
        .trim();
      if (lowered.length === 0) {
        projectResults = projects;
      } else {
        projectResults = projects.filter(p => {
          const nameMatch = p.name.toLowerCase().includes(lowered);
          const idMatch = String(p.projectId).includes(lowered);
          return nameMatch || idMatch;
        });
      }
      // Filter by display mode for project results too
      if (displayMode === 'archive') {
        projectResults = projectResults.filter(p => p.status === 'archived');
      } else {
        // For watchlist/active modes, show active projects
        projectResults = projectResults.filter(p => p.status === 'active');
      }
    }

    const totalResults =
      rankedPairs.length +
      searchedArbitrages.length +
      searchedBalancers.length +
      projectResults.length;

    return {
      pairs: rankedPairs,
      arbitrages: searchedArbitrages,
      balancers: searchedBalancers,
      projects: projectResults,
      allResults: [
        ...projectResults,
        ...rankedPairs,
        ...searchedArbitrages,
        ...searchedBalancers,
      ] as SearchResult[],
      isSearching: true,
      totalResults,
    };
  }, [
    searchQuery,
    allPairs,
    favoritePairsSet,
    favoriteArbitragesSet,
    favoriteBalancersSet,
    displayMode,
    typeFilters,
    platformFilters,
    arbitrageResults,
    balancerResults,
  ]);

  // Consider loading if projects haven't loaded yet OR if arbitrage/balancer queries are running
  const isLoading = !projectsLoaded || isLoadingArbitrages || isLoadingBalancers;

  // Calculate total pairs per project (for "All pairs" badge - should show total, not filtered count)
  const projectTotalPairs = useMemo(() => {
    const map = new Map<number, number>();
    (allPairs ?? []).forEach(pair => {
      map.set(pair.projectId, (map.get(pair.projectId) ?? 0) + 1);
    });
    return map;
  }, [allPairs]);

  const refreshProjectBotsData = useCallback(
    async (projectId: number) => {
      await Promise.all([refreshArbitrageProject(projectId), refreshBalancerProject(projectId)]);
    },
    [refreshArbitrageProject, refreshBalancerProject],
  );

  return {
    ...searchResults,
    isLoading,
    favoritePairsSet,
    favoriteArbitragesSet,
    favoriteBalancersSet,
    projectTotalPairs,
    refreshProjectBotsData,
  };
}
